import React, { useRef } from 'react';
import logoSrc from '../assets/ceocover.png';
import { Link } from 'react-router-dom';

export default function CEOMessagePage() {
  const ceoMessageRef = useRef(null);
  const contactCeoRef = useRef(null);

  const scrollToCeoMessage = () => {
    ceoMessageRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

 const scrollToContactCeo = () => {
  if (!contactCeoRef.current) return;

  const yOffset = -120; 
  const y =
    contactCeoRef.current.getBoundingClientRect().top +
    window.pageYOffset +
    yOffset;

  window.scrollTo({ top: y, behavior: 'smooth' });
};


  return (
    <div className="min-h-screen bg-[linear-gradient(135deg,#05001a,#0a0133,#140145)] antialiased">

      {/* HERO */}
      <div className="relative">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-28 flex flex-col items-center text-center">
          <div className="w-full max-w-4xl">

            <div className="mx-auto w-50 h-50 sm:w-72 sm:h-72 rounded-2xl flex items-center justify-center transition-all duration-700 hover:scale-[1.02]">
              <div className="relative w-full h-full flex items-center justify-center">
                <img
                  src={logoSrc}
                  alt="Cybredge logo"
                  className="object-contain w-full h-full drop-shadow-2xl"
                />
                <div
                  className="pointer-events-none absolute inset-0 rounded-xl blur-3xl opacity-30"
                  style={{ background: 'linear-gradient(90deg,#3b82f6,#ec4899)' }}
                />
              </div>
            </div>

            <h1 className="mt-8 text-white font-serif text-3xl lg:text-5xl font-semibold">
              Leadership that drives innovation & trust
            </h1>

            <p className="mt-4 text-white max-w-2xl mx-auto text-sm">
              Building intelligent, future-ready solutions that empower enterprises to thrive in the digital age.
            </p>

            <div className="mt-8 flex items-center justify-center gap-4">
              <button
              type="button"
                onClick={scrollToCeoMessage}
                className="inline-flex bg-white/90 text-blue-700 px-5 py-3 rounded-md font-medium shadow hover:scale-[1.01]"
              >
                Read CEO’s Message
              </button>

              <button
              type="button"
                onClick={scrollToContactCeo}
                className="inline-flex border border-white/30 text-[#fd02fd] px-5 py-3 rounded-md hover:bg-white/10"
              >
                Contact Leadership
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* CEO MESSAGE HEADER */}
      <div
        ref={ceoMessageRef}
        className="max-w-6xl mx-auto px-6 lg:px-8 mt-16"
      >
        <h2 className="text-3xl font-serif font-semibold text-white">
          A Message from Our CEO
        </h2>

        <p className="mt-3 text-white/80 text-sm leading-relaxed">
          Leadership rooted in integrity, innovation, and the relentless pursuit of excellence.
        </p>

        <div className="mt-4 w-20 h-1 bg-gradient-to-r from-indigo-600 to-pink-500 rounded-full" />
      </div>

      {/* CEO MESSAGE CONTENT */}
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-12">
        <div className="rounded-2xl bg-white shadow-lg p-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

            {/* TEXT */}
            <div className="lg:col-span-8 space-y-4 text-gray-700">
              <h2 className="font-serif text-2xl lg:text-3xl">CEO’s Message</h2>

              <p>
                “At Cybredge Private Limited, our journey is defined by innovation, integrity, and a relentless
                commitment to excellence. Technology, for us, is not just a tool — it is a catalyst that drives
                transformation, scalability, and remarkable business outcomes.
              </p>

              <p>
                Our team of passionate professionals fuels this mission. They bring creativity, precision, and deep
                expertise, enabling us to craft solutions that are not only efficient but truly transformative.
              </p>

              <p>
                Trust remains our strongest foundation. We believe in transparent partnerships, long-term relationships,
                and delivering sustainable value that elevates business performance and long-term growth.
              </p>

              <p>
                Our vision is to become a global leader in technology innovation — consistently delivering world-class
                digital solutions with unmatched standards of quality, service, and reliability.
              </p>

              <p>
                Thank you for being a part of our journey. Together, we will continue shaping a future where technology
                empowers every ambition.”
              </p>

              <div className="mt-6">
                <p className="font-semibold">— M.S. Siddiqui</p>
                <p className="text-sm text-gray-500">
                  Chief Executive Officer (CEO), Cybredge Private Limited
                </p>
              </div>
            </div>

            {/* SIDEBAR */}
            <div className="lg:col-span-4">
              <div className="sticky top-24 rounded-lg bg-gradient-to-b from-white to-indigo-50 p-6 border border-gray-100 shadow-sm">
                <h3 className="text-lg font-semibold">Our Vision</h3>

                <p className="mt-2 text-sm text-gray-600">
                  To become a global leader in technology innovation — delivering digital excellence at scale.
                </p>

                <dl className="mt-4 space-y-3">
                  <div>
                    <dt className="font-medium">Innovation</dt>
                    <dd className="text-sm text-gray-600">
                      Engineering breakthroughs that define the future.
                    </dd>
                  </div>

                  <div>
                    <dt className="font-medium">Trust</dt>
                    <dd className="text-sm text-gray-600">
                      Transparent, dependable, long-term partnerships.
                    </dd>
                  </div>

                  <div>
                    <dt className="font-medium">Sustainable Value</dt>
                    <dd className="text-sm text-gray-600">
                      Solutions that accelerate lasting business growth.
                    </dd>
                  </div>
                </dl>

                <div className="mt-6">
                  <Link
                    to="/contact"
                    className="inline-block bg-blue-800 text-white px-4 py-2 rounded-md"
                  >
                    Request a brochure
                  </Link>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* CONTACT CEO */}
      <div
        ref={contactCeoRef}
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
      >
        <div className="rounded-2xl bg-white p-5 sm:p-8 shadow-sm">
          <h3 className="text-base sm:text-lg font-semibold">
            Get in touch
          </h3>

          <p className="mt-2 text-sm text-gray-600">
            Have questions or want to collaborate? Our leadership team would love to connect.
          </p>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="p-4 border rounded-lg">
              <p className="text-sm font-semibold">Email</p>
              <a
                href="mailto:cyber.edge@yandex.com"
                className="text-sm text-indigo-600 break-all hover:underline"
              >
                cyber.edge@yandex.com
              </a>
            </div>

            <div className="p-4 border rounded-lg">
              <p className="text-sm font-semibold">Phone</p>
              <a
                href="tel:+919170802934"
                className="text-sm text-indigo-600 hover:underline"
              >
                +91 91708 02934
              </a>
            </div>

            <div className="p-4 border rounded-lg">
              <p className="text-sm font-semibold">Office</p>
              <p className="text-sm text-gray-600">
                Ghazipur, UP, India
              </p>
            </div>
          </div>

          <div className="mt-6">
            <Link
              to="/contact"
              className="block sm:inline-block text-center bg-[#fd02fd] text-white px-5 py-3 rounded-md w-full sm:w-auto"
            >
              Request a meeting
            </Link>
          </div>
        </div>
      </div>

    </div>
  );
}
